import ChartVariant from '@opsbase/lib-chart/ChartVariant';
import QueryFieldKey from '@opsbase/lib-data-queryfield/QueryFieldKey';
import generateViewFromQuery from '@opsbase/lib-data-view/helpers/generateViewFromQuery';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import calcTimeInterval from '@opsbase/lib-time/helpers/calcTimeInterval';
import useCss from '@opsbase/web-css';
import DataTreeController from '@opsbase/web-ctrl-datatree/DataTreeController';
import useViewRegistryActions from '@opsbase/web-ctrl-view/useViewRegistryActions';
import CountGroupChart from '@opsbase/web-data-chart/CountGroupChart';
import CountGroupTimeChart from '@opsbase/web-data-chart/CountGroupTimeChart';
import CountTimeChart from '@opsbase/web-data-chart/CountTimeChart';
import PerfGroupTimeChart from '@opsbase/web-data-chart/PerfGroupTimeChart';
import ScoreQOpChart from '@opsbase/web-data-chart/ScoreQOpChart';
import DeleteIcon from '@opsbase/web-icons/icons/DeleteIcon';
import ZoomIcon from '@opsbase/web-icons/icons/ZoomIcon';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import TextField from '@opsbase/web-textfield/TextField';
import IconButton from '@opsbase/web-ui/IconButton';
import Text from '@opsbase/web-ui/Text';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import OpsLogOpener from '../../logopeners/OpsLogOpener';
const makeMyClasses = () => ({
    deleteButton: {
        position: 'absolute',
        bottom: 4,
        left: 4,
        backgroundColor: Palette.BackgroundGrey.main,
        '&:hover': {
            opacity: 1,
            '& .SVGIconRoot': { color: Palette.error.main + ' !important' },
        },
    },
    openButton: {
        backgroundColor: Palette.BackgroundGrey.main,
        '&:hover': {
            opacity: 1,
        },
    },
});
export function DashboardWidgetViewer(props) {
    const { isEditable = false, dashboardWidget, onDelete, onRename, onOpen } = props;
    const { chart, query } = dashboardWidget;
    const { putView } = useViewRegistryActions();
    const [viewId, setViewId] = useState();
    const [opsLogOpenerProps, setOpsLogOpenerProps] = useState();
    const [title, setTitle] = useState(dashboardWidget.title);
    const isOpenable = !!onOpen && !!viewId;
    const myCss = useCss(makeMyClasses);
    useEffect(() => {
        if (query && !viewId) {
            const view = generateViewFromQuery(query);
            view.chartBox.visible = true;
            view.isDashboard = true;
            putView(view);
            setViewId(view.id);
        }
    }, [putView, viewId, query]);
    const handleOpen = useCallback(() => {
        if (!isOpenable)
            return;
        onOpen === null || onOpen === void 0 ? void 0 : onOpen(viewId);
    }, [isOpenable, viewId, onOpen]);
    const handleRename = useCallback(() => {
        if (title !== dashboardWidget.title)
            onRename === null || onRename === void 0 ? void 0 : onRename(dashboardWidget, title);
    }, [dashboardWidget, onRename, title]);
    const handleRemove = useCallback(() => onDelete === null || onDelete === void 0 ? void 0 : onDelete(dashboardWidget), [dashboardWidget, onDelete]);
    const handleClickValue = useCallback(v => {
        switch (chart.chartVariant) {
            case ChartVariant.COUNT_GROUP:
            case ChartVariant.COUNT_TIME:
            case ChartVariant.COUNT_GROUP_TIME:
            case ChartVariant.SCORE_Q_OP:
                // DO NOTHING
                break;
            case ChartVariant.PERF_GROUP_TIME:
                const { dimension, interval } = chart.config;
                const opDef = dimension === QueryFieldKey.OPDEFINITION;
                const opDefinitionId = opDef ? v.y.id : undefined;
                const placeId = opDef ? undefined : v.y.id;
                const t = calcTimeInterval(interval, moment(v.x.id));
                const startFrom = [t.from, t.to];
                setOpsLogOpenerProps({ opDefinitionId, placeId, startFrom });
        }
    }, [chart.config, chart.chartVariant]);
    const chartBit = useMemo(() => {
        const chartProps = {
            viewId,
            chart,
            onClickValue: handleClickValue,
        };
        switch (chart.chartVariant) {
            case ChartVariant.COUNT_GROUP:
                if (!viewId)
                    return null;
                return (React.createElement(Pane, { grow: true },
                    React.createElement(DataTreeController, { viewId: viewId },
                        React.createElement(CountGroupChart, { ...chartProps }))));
            case ChartVariant.COUNT_TIME:
                if (!viewId)
                    return null;
                return (React.createElement(Pane, { grow: true },
                    React.createElement(DataTreeController, { viewId: viewId },
                        React.createElement(CountTimeChart, { ...chartProps }))));
            case ChartVariant.COUNT_GROUP_TIME:
                if (!viewId)
                    return null;
                return (React.createElement(Pane, { grow: true },
                    React.createElement(DataTreeController, { viewId: viewId },
                        React.createElement(CountGroupTimeChart, { ...chartProps }))));
            case ChartVariant.SCORE_Q_OP:
                if (!viewId)
                    return null;
                return (React.createElement(Pane, { grow: true },
                    React.createElement(DataTreeController, { viewId: viewId },
                        React.createElement(ScoreQOpChart, { ...chartProps }))));
            case ChartVariant.PERF_GROUP_TIME:
                return React.createElement(PerfGroupTimeChart, { ...chartProps });
        }
    }, [chart, handleClickValue, viewId]);
    const titleBit = useMemo(() => {
        return (React.createElement(Horizontal, { shrink: true },
            !!opsLogOpenerProps && React.createElement(OpsLogOpener, { ...opsLogOpenerProps }),
            isEditable && (React.createElement(TextField, { className: "dragcancel", noBorder: true, multiline: true, fullWidth: true, noMargin: true, inputProps: {
                    style: { fontSize: 12 },
                }, InputProps: {
                    style: { padding: 0 },
                }, value: title, placeholder: i18n.t('enter-title'), onChange: setTitle, onBlur: handleRename })),
            !isEditable && React.createElement(Text, { variant: "h2" }, title),
            isOpenable && (React.createElement(Pane, null,
                React.createElement(IconButton, { size: 24, icon: React.createElement(ZoomIcon, { color: Palette.GreyBlue, size: 18 }), classes: { root: myCss.openButton + ' dragcancel' }, tooltip: i18n.t('view-data'), tooltipDelay: 100, onClick: handleOpen })))));
    }, [myCss, opsLogOpenerProps, handleOpen, handleRename, isEditable, isOpenable, title]);
    return useMemo(() => {
        return (React.createElement(Vertical, null,
            titleBit,
            chartBit,
            isEditable && (React.createElement(IconButton, { size: 20, icon: React.createElement(DeleteIcon, { color: Palette.GreyBlue, size: 14 }), classes: { root: myCss.deleteButton + ' dragcancel' }, tooltip: i18n.t('remove-from-dashboard'), tooltipDelay: 100, onClick: handleRemove }))));
    }, [chartBit, handleRemove, isEditable, myCss, titleBit]);
}
export default DashboardWidgetViewer;
